/**
 * MIKUNA — Auto-add Free Chimborazo Shaker (v2)
 * Defensive against race conditions and duplicates.
 */
(function () {
  'use strict';

  const CHOCHO_HANDLE = 'chocho-plant-protein-by-mikuna';
  const SHAKER_HANDLE = 'chimborazo-shaker-bottle';
  const THRESHOLD = 3;
  const GIFT_PROP = '_auto_gift';

  let shakerVariantId = null;
  let isReconciling = false;
  const originalFetch = window.fetch.bind(window);

  const getShakerVariantId = async () => {
    if (shakerVariantId) return shakerVariantId;
    try {
      const res = await originalFetch(`/products/${SHAKER_HANDLE}.js`);
      if (!res.ok) return null;
      const product = await res.json();
      const variant = product.variants.find(v => v.available) || product.variants[0];
      shakerVariantId = variant ? variant.id : null;
      return shakerVariantId;
    } catch (e) {
      return null;
    }
  };

  const getCart = async () => {
    try {
      const res = await originalFetch('/cart.js', {
        headers: { 'Accept': 'application/json' }
      });
      return res.ok ? await res.json() : null;
    } catch (e) {
      return null;
    }
  };

  const ONETIME_THRESHOLD = 3;
  const SUB_THRESHOLD = 2;

  const countOneTimeChocho = (cart) => {
    if (!cart || !cart.items) return 0;
    return cart.items.reduce((sum, item) => {
      if (item.handle !== CHOCHO_HANDLE) return sum;
      if (item.selling_plan_allocation) return sum;
      return sum + item.quantity;
    }, 0);
  };

  const countSubscriptionChocho = (cart) => {
    if (!cart || !cart.items) return 0;
    return cart.items.reduce((sum, item) => {
      if (item.handle !== CHOCHO_HANDLE) return sum;
      if (!item.selling_plan_allocation) return sum;
      return sum + item.quantity;
    }, 0);
  };

  const qualifiesForGift = (cart) => {
    return countOneTimeChocho(cart) >= ONETIME_THRESHOLD ||
           countSubscriptionChocho(cart) >= SUB_THRESHOLD;
  };

  const findAutoShakers = (cart) => {
    if (!cart || !cart.items) return [];
    return cart.items.filter(item =>
      item.handle === SHAKER_HANDLE &&
      item.properties &&
      item.properties[GIFT_PROP] === 'true'
    );
  };

  const findAnyShaker = (cart) => {
    if (!cart || !cart.items) return null;
    return cart.items.find(item => item.handle === SHAKER_HANDLE);
  };

  const addAutoShaker = async () => {
    const variantId = await getShakerVariantId();
    if (!variantId) return;

    // Defensive re-check immediately before adding
    const freshCart = await getCart();
    if (findAnyShaker(freshCart)) {
      console.log('[Mikuna gift] Shaker already in cart, skipping add');
      return;
    }

    try {
      await originalFetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: 1,
            properties: {
              [GIFT_PROP]: 'true',
              'Gift': 'Free with 3+ bags'
            }
          }]
        })
      });
    } catch (e) {
      console.error('[Mikuna gift] Failed to add shaker:', e);
    }
  };

  const changeLineQty = async (lineKey, quantity) => {
    try {
      await originalFetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity })
      });
    } catch (e) {
      console.error('[Mikuna gift] Failed to change line:', e);
    }
  };

const refreshCartUI = () => {
    // Dispatch every common cart event that themes listen for
    [
      'cart:refresh', 'cart:updated', 'cart:change', 'cart:build',
      'cart:rerender', 'cart:item-added', 'theme:cart:change'
    ].forEach(name => {
      document.dispatchEvent(new CustomEvent(name, { bubbles: true }));
      window.dispatchEvent(new CustomEvent(name));
    });

    // If we're on the cart page, force a reload so the user sees the shaker
    if (window.location.pathname === '/cart' ||
        window.location.pathname.startsWith('/cart?')) {
      setTimeout(() => window.location.reload(), 300);
    }
  };

  const reconcile = async () => {
    if (isReconciling) return;
    isReconciling = true;
    try {
      const cart = await getCart();
      if (!cart) return;

const qualifies = qualifiesForGift(cart);
      const autoShakers = findAutoShakers(cart);
      const anyShaker = findAnyShaker(cart);

      if (qualifies) {
        if (!anyShaker) {
          await addAutoShaker();
          refreshCartUI();
        } else if (autoShakers.length > 1) {
          // Multiple auto-shaker line items — remove extras
          for (let i = 1; i < autoShakers.length; i++) {
            await changeLineQty(autoShakers[i].key, 0);
          }
          refreshCartUI();
        } else if (autoShakers.length === 1 && autoShakers[0].quantity > 1) {
          // Auto-shaker qty crept above 1 — reset to 1
          await changeLineQty(autoShakers[0].key, 1);
          refreshCartUI();
        }
      } else {
        if (autoShakers.length > 0) {
          for (const s of autoShakers) {
            await changeLineQty(s.key, 0);
          }
          refreshCartUI();
        }
      }
    } finally {
      isReconciling = false;
    }
  };

  window.fetch = function (...args) {
    const url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
    const result = originalFetch.apply(this, args);

    if (/\/cart\/(add|change|update|clear)(?:\.js)?(?:\?|$)/.test(url)) {
      result.then(() => {
        if (!isReconciling) setTimeout(reconcile, 200);
      }).catch(() => {});
    }

    return result;
  };

  const init = () => setTimeout(reconcile, 400);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();